import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronLeft, FiChevronRight, FiCalendar } from "react-icons/fi";
import { toast } from "react-toastify";
import { calendarAPI } from "../services/api";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  NEPALI_WEEKDAYS,
  NEPALI_MONTHS,
  toNepaliDigits,
  isWeekendIndex,
} from "../utils/nepaliCalendar";

const NepaliCalendar = () => {
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null); // 1-12
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    calendarAPI
      .getToday()
      .then((res) => {
        const t = res.data?.data;
        setYear(t.bsYear);
        setMonth(t.bsMonth);
      })
      .catch(() => toast.error("Failed to load calendar"));
  }, []);

  const loadMonth = useCallback(() => {
    if (!year || !month) return;
    setLoading(true);
    calendarAPI
      .getMonth(year, month)
      .then((res) => setMonthData(res.data?.data))
      .catch(() => toast.error("Failed to load calendar"))
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const goPrev = () => {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goNext = () => {
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const goToday = () => {
    calendarAPI
      .getToday()
      .then((res) => {
        const t = res.data?.data;
        setYear(t.bsYear);
        setMonth(t.bsMonth);
      })
      .catch(() => {});
  };

  if (!year || !month) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const cells = [];
  if (monthData) {
    for (let i = 0; i < monthData.startWeekday; i++) cells.push(null);
    for (let d = 1; d <= monthData.daysInMonth; d++) cells.push(d);
  }

  const eventsByDay = {};
  (monthData?.events || []).forEach((e) => {
    if (!eventsByDay[e.bsDay]) eventsByDay[e.bsDay] = [];
    eventsByDay[e.bsDay].push(e);
  });

  // Side panel follows the displayed month; in the current month, hide days already passed
  const todayDay = monthData?.today?.day;
  const monthEvents = (monthData?.events || []).filter(
    (e) => !todayDay || e.bsDay >= todayDay
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-serif font-bold text-primary-600">
          School Calendar
        </h1>
        <p className="text-gray-500 mt-2 text-sm sm:text-base">
          Bikram Sambat (BS) academic calendar &amp; holidays
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-start">
        {/* Calendar card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-primary-600">
              {toNepaliDigits(year)} {monthData?.monthNameEn || ""}
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={goPrev}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                aria-label="Previous month"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={goToday}
                className="px-2.5 py-1.5 rounded-lg hover:bg-gray-100 text-gray-500 text-xs font-medium transition-colors"
              >
                Today
              </button>
              <button
                onClick={goNext}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                aria-label="Next month"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          {monthData?.today && (
            <p className="text-gray-400 text-sm mb-4">
              Today: {toNepaliDigits(monthData.today.day)}
            </p>
          )}

          {loading ? (
            <div className="py-10">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1 text-center text-xs sm:text-sm font-semibold mb-2">
                {NEPALI_WEEKDAYS.map((d, i) => (
                  <div
                    key={i}
                    className={isWeekendIndex(i) ? "text-red-500" : "text-gray-400"}
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((day, idx) => {
                  if (day === null) return <div key={idx} />;
                  const weekday = (monthData.startWeekday + (day - 1)) % 7;
                  const weekend = isWeekendIndex(weekday);
                  const dayEvents = eventsByDay[day] || [];
                  const hasHoliday = weekend || dayEvents.some((e) => e.isHoliday);
                  const hasEvent = dayEvents.some((e) => !e.isHoliday);
                  const isToday = monthData.today?.day === day;

                  return (
                    <div
                      key={idx}
                      title={dayEvents.map((e) => e.title).join(", ")}
                      className={`relative aspect-square flex flex-col items-center justify-center rounded-xl text-sm sm:text-base border transition-colors ${
                        isToday
                          ? "border-primary-500 ring-1 ring-primary-300"
                          : "border-transparent"
                      } ${
                        hasHoliday
                          ? "bg-red-50 text-red-600 font-semibold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {toNepaliDigits(day)}
                      {dayEvents.length > 0 && (
                        <span
                          className={`absolute bottom-1 sm:bottom-1.5 w-1.5 h-1.5 rounded-full ${
                            hasHoliday && dayEvents.some((e) => e.isHoliday)
                              ? "bg-red-500"
                              : hasEvent
                                ? "bg-gray-400"
                                : "bg-transparent"
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-5 pt-4 border-t border-gray-50 text-xs text-gray-400">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500" /> Holiday
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-gray-400" /> Event
                </span>
              </div>
            </>
          )}
        </div>

        {/* Coming up */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
            {todayDay ? "Coming Up in" : "Events in"} {monthData?.monthNameEn}
          </h3>
          {loading ? (
            <div className="py-10">
              <LoadingSpinner />
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={`${monthData?.year}-${monthData?.month}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                {monthEvents.length === 0 && (
                  <div className="text-center py-10">
                    <FiCalendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">
                      No {todayDay ? "upcoming " : ""}events in{" "}
                      {monthData?.monthNameEn}
                    </p>
                  </div>
                )}
                {monthEvents.map((e) => (
                  <div key={e._id} className="flex items-start gap-3">
                    <div
                      className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl border shrink-0 ${
                        e.isHoliday
                          ? "bg-red-50 border-red-100 text-red-600"
                          : "bg-gray-50 border-gray-100 text-gray-500"
                      }`}
                    >
                      <span className="text-lg font-bold leading-none">
                        {toNepaliDigits(e.bsDay)}
                      </span>
                      <span className="text-[10px] mt-0.5">
                        {NEPALI_MONTHS[monthData.month - 1]}
                      </span>
                    </div>
                    <div className="pt-1.5">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                            e.isHoliday ? "bg-red-500" : "bg-gray-400"
                          }`}
                        />
                        <span className="text-sm text-gray-700 font-medium">
                          {e.title}
                        </span>
                      </div>
                      {e.description && (
                        <p className="text-xs text-gray-400 mt-0.5 ml-3">
                          {e.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};

export default NepaliCalendar;
